import { loader } from "@monaco-editor/react";

let monacoPromise: Promise<unknown> | null = null;

/**
 * Kick off Monaco's download (/monaco-v3/vs/loader.js → editor.main → the
 * language chunk; the path itself is `monacoLoader.config` in main.tsx, the
 * ONE place it lives) once and memoize it. `loader.init()` is an idempotent, NON-resettable
 * singleton, and the `<Editor>` mount in CodeQuestion calls the same
 * `loader.init()` — so preloading here during Gate means the first code
 * question's editor resolves against an already-downloaded Monaco instead of
 * stalling ~570ms (prod 1-2s+) on a cold fetch.
 *
 * No reset-on-reject (unlike `loadModels`): the loader sets its internal
 * `isInitialized` flag BEFORE the download starts and never clears it, so it
 * can't be re-armed after a failed init — nulling the promise would be
 * pointless. This mirrors `getPyodide()`. `.config({ paths })` already ran at
 * app boot in main.tsx before this is ever called at runtime, so the vendor
 * path is set.
 */
export function preloadMonaco(): Promise<unknown> {
  if (!monacoPromise) {
    monacoPromise = loader.init();
  }
  return monacoPromise;
}

type TsDiagnosticsDefaults = {
  setDiagnosticsOptions(options: { noSemanticValidation?: boolean; noSyntaxValidation?: boolean }): void;
};

/**
 * FEAT-12 Build 2 — the `beforeMount` of every code editor. TypeScript's types
 * are erased before the code runs and are never checked (tsStrip.js, the same
 * in ts_grader.js), so the editor must not paint type errors the grader
 * ignores: syntax errors only, as JavaScript already gets by default. A
 * global default of the one Monaco instance, so calling it on every mount is
 * harmless. The vendored bundle attaches the TypeScript API at
 * `languages.typescript`; newer typings name it `typescript` — read both.
 */
export function configureMonaco(monaco: unknown): void {
  const api = monaco as {
    typescript?: { typescriptDefaults?: TsDiagnosticsDefaults };
    languages?: { typescript?: { typescriptDefaults?: TsDiagnosticsDefaults } };
  };
  const defaults = (api.languages?.typescript ?? api.typescript)?.typescriptDefaults;
  defaults?.setDiagnosticsOptions({ noSemanticValidation: true, noSyntaxValidation: false });
}
